/**
 * Comments store — spec §10.3.
 *
 * Keeps per-target comments and fix-action overlays. One store serves every
 * comment target: draft artifacts (anchored, inline) and the extraction kinds —
 * compliance requirements, risk-register items and evaluation criteria
 * (whole-object threads). The maps are keyed by plain target id — artifact,
 * compliance-item, risk-item and criterion ids are all UUIDs, so they cannot
 * collide — and the discriminator rides on the *actions*, which pick the API
 * family.
 */

import { create } from 'zustand'
import * as commentsApi from '@/lib/comments-api'
import type {
  Comment,
  CommentPermissions,
  CommentFixAction,
  CommentTargetType,
  ExtractionCommentTargetType,
  CreateCommentInput,
  RequirementCommentCounts,
  SourceFindingRef,
  CommentReply,
} from '@/lib/comments-api'

export interface CommentsStoreState {
  byTargetId: Record<string, Comment[]>
  fixActionsByTargetId: Record<string, Record<string, CommentFixAction>>
  loading: Record<string, boolean>

  /**
   * Per target. A global string would let one target's failed load paint an
   * error over another's healthy list — and, worse, let a background poll for
   * target A silently wipe the error the user is currently reading on B.
   */
  error: Record<string, string | null>

  /**
   * This viewer's last "opened the comments surface" timestamp per target (null
   * if never opened) — the unread cut-line, served by the list endpoint.
   */
  watermarkByTargetId: Record<string, string | null>

  /**
   * Frozen copy of the watermark taken when the comments surface was opened,
   * per target (spec D4).
   *
   * The watermark advances the instant the tab is opened, so reading unread off
   * it would clear every "New" pill in the same frame the user arrived to read
   * them. The snapshot is the cut-line the *user* sees: taken once per visit,
   * before the viewed-POST, and never touched again by polls — so items that
   * arrive while the tab sits open stay marked new until the user leaves it.
   *
   * Key presence — not the value — is the "comments surface is live" signal: an
   * absent key means no view session (reference falls back to the watermark),
   * a present `null` means the session opened on a never-viewed target.
   */
  viewSnapshotByTargetId: Record<string, string | null>

  /**
   * Per-item thread counts for the open bid, keyed by extraction-item id —
   * compliance items, risk-register items and evaluation criteria share the
   * map, since all are UUIDs and cannot collide. Populated per family by
   * {@link CommentsStoreState.loadRequirementCounts} so each table can badge
   * every row, then nudged optimistically by whole-object mutations so a badge
   * tracks without waiting for a refetch.
   */
  countsByItemId: Record<string, RequirementCommentCounts>

  // Permissions are cached per `${targetId}:${sectionId ?? '__artifact__'}`.
  permissions: Record<string, CommentPermissions>

  // Set of issue keys that have been promoted to comments (per target).
  // Derived from sourceFindingRef on loaded comments; comments promoted before
  // the issue model shipped fall back to their `runId:findingIndex` key.
  promotedFromReview: Record<string, Set<string>>

  // Currently-focused comment (UI highlight).
  focusedCommentId: string | null

  // ----- Actions -----
  load: (
    bidId: string,
    targetId: string,
    status?: 'open' | 'resolved' | 'all',
    opts?: { silent?: boolean; targetType?: CommentTargetType },
  ) => Promise<void>
  loadPermissions: (
    bidId: string,
    targetId: string,
    sectionId?: string | null,
    targetType?: CommentTargetType,
  ) => Promise<CommentPermissions>
  create: (
    bidId: string,
    targetId: string,
    input: CreateCommentInput,
    targetType?: CommentTargetType,
  ) => Promise<Comment>
  update: (
    bidId: string,
    targetId: string,
    commentId: string,
    body: string,
    targetType?: CommentTargetType,
  ) => Promise<void>
  remove: (
    bidId: string,
    targetId: string,
    commentId: string,
    targetType?: CommentTargetType,
  ) => Promise<void>
  resolve: (
    bidId: string,
    targetId: string,
    commentId: string,
    targetType?: CommentTargetType,
  ) => Promise<void>
  reopen: (
    bidId: string,
    targetId: string,
    commentId: string,
    targetType?: CommentTargetType,
  ) => Promise<void>
  addReply: (
    bidId: string,
    targetId: string,
    commentId: string,
    body: string,
    targetType?: CommentTargetType,
    /** Mentioned colleagues to add to the bid first (draft threads only). */
    addToBid?: string[],
  ) => Promise<void>
  updateReply: (
    bidId: string,
    targetId: string,
    commentId: string,
    replyId: string,
    body: string,
    targetType?: CommentTargetType,
  ) => Promise<void>
  removeReply: (
    bidId: string,
    targetId: string,
    commentId: string,
    replyId: string,
    targetType?: CommentTargetType,
  ) => Promise<void>
  /** Artifact-only: requirement permissions always report `canResolveWithAI: false`. */
  resolveWithAI: (
    bidId: string,
    artifactId: string,
    commentId: string,
  ) => Promise<{ threadId: string; actionId: string; threadSeed: string }>
  promoteFromFinding: (
    bidId: string,
    artifactId: string,
    input: {
      sectionId: string | null
      quotedText: string | null
      startPos: number | null
      endPos: number | null
      body: string
      sourceFindingRef: SourceFindingRef
    },
  ) => Promise<Comment>

  markViewed: (
    bidId: string,
    targetId: string,
    targetType?: CommentTargetType,
  ) => Promise<void>
  clearViewSnapshot: (targetId: string) => void

  loadRequirementCounts: (
    bidId: string,
    targetType?: ExtractionCommentTargetType,
  ) => Promise<void>

  focusComment: (commentId: string | null) => void
  clearError: (targetId: string) => void
  reset: () => void
}

/**
 * Human name per extraction kind, for the anchored-create guard's message.
 *
 * A map rather than a ternary: with three kinds a two-arm conditional silently
 * mislabels whichever one it doesn't name, and the whole point of throwing here
 * instead of letting the server 400 is that the message names the real mistake.
 */
const EXTRACTION_TARGET_LABELS: Record<ExtractionCommentTargetType, string> = {
  requirement: 'Requirement',
  risk: 'Risk',
  criterion: 'Criterion',
}

const permKey = (targetId: string, sectionId: string | null | undefined) =>
  `${targetId}:${sectionId ?? '__artifact__'}`

/**
 * In-flight permission requests, keyed by {@link permKey}.
 *
 * Every CommentCard, CommentBubbleButton and CommentsTab loads permissions on
 * mount, so one section-editor page load fired ~18 identical parallel GETs.
 * Concurrent callers for the same key now share a single promise.
 *
 * Strictly in-flight-only: the entry is deleted the moment the request settles,
 * on success *and* on failure. It is a request coalescer, never a cache — a
 * transient 503 must not be pinned into the session (see the deny-all fallback
 * below), and a later mount must be free to refetch.
 */
const inflightPermissions = new Map<string, Promise<CommentPermissions>>()

/**
 * Focused-comment lifetime (spec A2).
 *
 * A focus highlight is a "look here" cue, not a mode: it expires on its own
 * after {@link FOCUS_CLEAR_MS} and dies the moment the user points anywhere.
 * The pointer listener is armed on the next tick so the very click that set
 * the focus doesn't immediately clear it.
 */
const FOCUS_CLEAR_MS = 6000

let focusExpiryTimer: ReturnType<typeof setTimeout> | null = null
let focusArmTimer: ReturnType<typeof setTimeout> | null = null
let focusPointerListener: (() => void) | null = null

function clearFocusWatchers(): void {
  if (focusExpiryTimer !== null) {
    clearTimeout(focusExpiryTimer)
    focusExpiryTimer = null
  }
  if (focusArmTimer !== null) {
    clearTimeout(focusArmTimer)
    focusArmTimer = null
  }
  if (focusPointerListener !== null) {
    window.removeEventListener('pointerdown', focusPointerListener)
    focusPointerListener = null
  }
}

/**
 * Mutation epoch, per target (spec D3).
 *
 * The 30 s poll and the user's own edits race: a GET issued at t=0 can land
 * after a create/resolve at t=0.4s, and its body — a snapshot of the world
 * *before* that mutation — would silently undo it on screen. The counter is
 * bumped at the start of every mutation; `load` captures it at request start
 * and discards any response whose epoch has moved. Same shape as agent-store's
 * monotonic request-id gates.
 *
 * Module-level, not store state: it must not trigger re-renders, and it must
 * survive `reset()` so a load in flight across a reset is still comparable.
 */
const mutationEpoch: Record<string, number> = {}

function bumpMutationEpoch(targetId: string): void {
  mutationEpoch[targetId] = (mutationEpoch[targetId] ?? 0) + 1
}

/**
 * Key one promoted comment by the issue it came from. The `runId:findingIndex`
 * fallback keeps comments promoted before the issue model shipped showing as
 * promoted in the editor.
 */
function promotionKey(ref: SourceFindingRef): string {
  return ref.issueKey ?? `${ref.runId}:${ref.findingIndex}`
}

function computePromotedSet(comments: Comment[]): Set<string> {
  const out = new Set<string>()
  for (const c of comments) {
    if (c.sourceFindingRef) {
      out.add(promotionKey(c.sourceFindingRef))
    }
  }
  return out
}

/**
 * Nudge one item's badge counts without a refetch.
 *
 * The compliance table's thread badge is read from `countsByItemId`, which is
 * loaded once per bid. Without this the badge would sit stale until the next
 * bid-wide reload — long enough for a user to post a comment and see nothing
 * change. Clamped at zero: a delete racing a stale count must never render a
 * negative badge.
 */
function applyCountDelta(
  map: Record<string, RequirementCommentCounts>,
  itemId: string,
  delta: { open?: number; total?: number },
): Record<string, RequirementCommentCounts> {
  const prev = map[itemId] ?? {
    itemId,
    open: 0,
    total: 0,
    lastActivityAt: new Date().toISOString(),
    watermark: null,
  }
  return {
    ...map,
    [itemId]: {
      ...prev,
      open: Math.max(0, prev.open + (delta.open ?? 0)),
      total: Math.max(0, prev.total + (delta.total ?? 0)),
      lastActivityAt: new Date().toISOString(),
    },
  }
}

export const useCommentsStore = create<CommentsStoreState>()((set, get) => ({
  byTargetId: {},
  fixActionsByTargetId: {},
  loading: {},
  error: {},
  watermarkByTargetId: {},
  viewSnapshotByTargetId: {},
  countsByItemId: {},
  permissions: {},
  promotedFromReview: {},
  focusedCommentId: null,

  /**
   * `silent: true` is the background-poll mode (spec D3). It refreshes the data
   * and nothing else — no loading flag (a zero-comment target would flash
   * "Loading comments…" every 30 s), no error writes, and last-good data is
   * kept when the request fails. A poll that can't reach the server is not
   * news the user asked for; the next tick retries.
   */
  load: async (bidId, targetId, status = 'all', opts) => {
    const silent = opts?.silent ?? false
    const targetType = opts?.targetType ?? 'draft'
    const epochAtStart = mutationEpoch[targetId] ?? 0

    if (!silent) {
      set((s) => ({
        loading: { ...s.loading, [targetId]: true },
        error: { ...s.error, [targetId]: null },
      }))
    }

    try {
      // The requirement envelope carries no `fixActions` key at all — there is
      // no Resolve-with-AI lifecycle on a requirement thread — so default it
      // rather than reading an absent field.
      let comments: Comment[]
      let watermark: string | null
      let fixActions: Record<string, CommentFixAction>
      if (targetType !== 'draft') {
        const res = await commentsApi.fetchRequirementComments(
          bidId,
          targetId,
          status,
          targetType,
        )
        comments = res.comments
        watermark = res.watermark
        fixActions = {}
      } else {
        const res = await commentsApi.listComments(bidId, targetId, status)
        comments = res.comments
        watermark = res.watermark
        fixActions = res.fixActions
      }

      // A mutation landed while this request was in flight, so the body we're
      // holding predates it. Applying it would visibly undo the user's edit.
      if ((mutationEpoch[targetId] ?? 0) !== epochAtStart) {
        if (!silent) set((s) => ({ loading: { ...s.loading, [targetId]: false } }))
        return
      }

      set((s) => ({
        byTargetId: { ...s.byTargetId, [targetId]: comments },
        fixActionsByTargetId: { ...s.fixActionsByTargetId, [targetId]: fixActions },
        watermarkByTargetId: { ...s.watermarkByTargetId, [targetId]: watermark },
        promotedFromReview: {
          ...s.promotedFromReview,
          [targetId]: computePromotedSet(comments),
        },
        ...(silent ? {} : { loading: { ...s.loading, [targetId]: false } }),
      }))
    } catch (err) {
      if (silent) {
        console.warn('[comments-store] Background comments poll failed', err)
        return
      }
      set((s) => ({
        error: {
          ...s.error,
          [targetId]: err instanceof Error ? err.message : 'Failed to load comments',
        },
        loading: { ...s.loading, [targetId]: false },
      }))
    }
  },

  loadPermissions: async (bidId, targetId, sectionId, targetType = 'draft') => {
    const key = permKey(targetId, sectionId)

    const shared = inflightPermissions.get(key)
    if (shared) return shared

    // The shared promise is the *already-handled* one — it resolves to the
    // deny-all fallback rather than rejecting, so a piggybacking caller that
    // doesn't await still can't produce an unhandled rejection.
    const request = (async (): Promise<CommentPermissions> => {
      try {
        const perms =
          targetType !== 'draft'
            ? await commentsApi.fetchRequirementCommentPermissions(bidId, targetId, targetType)
            : await commentsApi.getPermissions(bidId, targetId, sectionId)
        set((s) => ({ permissions: { ...s.permissions, [key]: perms } }))
        return perms
      } catch (err) {
        // Permission loads are fire-and-forget from components — swallow so the
        // failure doesn't surface as an unhandled rejection in callers that
        // don't await. Return a deny-all fallback so the caller renders nothing
        // it can't prove the user may do.
        //
        // Deliberately NOT cached: writing deny-all into `permissions` would
        // pin a transient 503 into the session, silently stripping Reply /
        // Resolve with AI until a full reload. Leaving the entry unset means the
        // next mount refetches.
        const fallback: CommentPermissions = {
          effectiveRole: 'reader',
          canCreate: false,
          canReply: false,
          canResolve: false,
          canResolveWithAI: false,
          canPromoteFromReview: false,
          sectionCollaborationStatus: null,
          artifactStatus: 'draft',
        }
        void err
        return fallback
      }
    })().finally(() => {
      inflightPermissions.delete(key)
    })

    inflightPermissions.set(key, request)
    return request
  },

  create: async (bidId, targetId, input, targetType = 'draft') => {
    if (targetType !== 'draft') {
      const { sectionId, startPos, endPos, quotedText } = input.anchor
      if (sectionId !== null || startPos !== null || endPos !== null || quotedText !== null) {
        // The server 400s on this too, but failing here names the actual
        // mistake instead of surfacing a generic validation error two layers up.
        throw new Error(
          `${EXTRACTION_TARGET_LABELS[targetType]} comments are whole-object: ` +
            'an anchor cannot be attached to one',
        )
      }
    }

    bumpMutationEpoch(targetId)
    const comment =
      targetType !== 'draft'
        ? await commentsApi.createRequirementComment(bidId, targetId, input.body, targetType)
        : await commentsApi.createComment(bidId, targetId, input)
    set((s) => {
      const prev = s.byTargetId[targetId] ?? []
      const next = [...prev, comment]
      const promoted = new Set(s.promotedFromReview[targetId] ?? [])
      if (comment.sourceFindingRef) {
        promoted.add(promotionKey(comment.sourceFindingRef))
      }
      return {
        byTargetId: { ...s.byTargetId, [targetId]: next },
        promotedFromReview: { ...s.promotedFromReview, [targetId]: promoted },
        ...(targetType !== 'draft'
          ? { countsByItemId: applyCountDelta(s.countsByItemId, targetId, { open: 1, total: 1 }) }
          : {}),
      }
    })
    return comment
  },

  update: async (bidId, targetId, commentId, body, targetType = 'draft') => {
    bumpMutationEpoch(targetId)
    const updated =
      targetType !== 'draft'
        ? await commentsApi.updateRequirementComment(bidId, targetId, commentId, body, targetType)
        : await commentsApi.updateComment(bidId, targetId, commentId, body)
    set((s) => {
      const prev = s.byTargetId[targetId] ?? []
      return {
        byTargetId: {
          ...s.byTargetId,
          [targetId]: prev.map((c) => (c.id === commentId ? updated : c)),
        },
      }
    })
  },

  remove: async (bidId, targetId, commentId, targetType = 'draft') => {
    bumpMutationEpoch(targetId)
    // Read the status before the row leaves the store: a resolved thread only
    // decrements `total`, an open one decrements both.
    const wasOpen =
      get().byTargetId[targetId]?.find((c) => c.id === commentId)?.status !== 'resolved'
    if (targetType !== 'draft') {
      await commentsApi.deleteRequirementComment(bidId, targetId, commentId, targetType)
    } else {
      await commentsApi.deleteComment(bidId, targetId, commentId)
    }
    set((s) => {
      const prev = s.byTargetId[targetId] ?? []
      return {
        byTargetId: {
          ...s.byTargetId,
          [targetId]: prev.filter((c) => c.id !== commentId),
        },
        ...(targetType !== 'draft'
          ? {
              countsByItemId: applyCountDelta(s.countsByItemId, targetId, {
                total: -1,
                open: wasOpen ? -1 : 0,
              }),
            }
          : {}),
      }
    })
  },

  resolve: async (bidId, targetId, commentId, targetType = 'draft') => {
    bumpMutationEpoch(targetId)
    const updated =
      targetType !== 'draft'
        ? await commentsApi.resolveRequirementComment(bidId, targetId, commentId, targetType)
        : await commentsApi.resolveComment(bidId, targetId, commentId)
    set((s) => {
      const prev = s.byTargetId[targetId] ?? []
      return {
        byTargetId: {
          ...s.byTargetId,
          [targetId]: prev.map((c) => (c.id === commentId ? updated : c)),
        },
        ...(targetType !== 'draft'
          ? { countsByItemId: applyCountDelta(s.countsByItemId, targetId, { open: -1 }) }
          : {}),
      }
    })
  },

  reopen: async (bidId, targetId, commentId, targetType = 'draft') => {
    bumpMutationEpoch(targetId)
    const updated =
      targetType !== 'draft'
        ? await commentsApi.reopenRequirementComment(bidId, targetId, commentId, targetType)
        : await commentsApi.reopenComment(bidId, targetId, commentId)
    set((s) => {
      const prev = s.byTargetId[targetId] ?? []
      return {
        byTargetId: {
          ...s.byTargetId,
          [targetId]: prev.map((c) => (c.id === commentId ? updated : c)),
        },
        ...(targetType !== 'draft'
          ? { countsByItemId: applyCountDelta(s.countsByItemId, targetId, { open: 1 }) }
          : {}),
      }
    })
  },

  addReply: async (bidId, targetId, commentId, body, targetType = 'draft', addToBid) => {
    bumpMutationEpoch(targetId)
    const reply =
      targetType !== 'draft'
        ? await commentsApi.addRequirementCommentReply(
            bidId,
            targetId,
            commentId,
            body,
            targetType,
          )
        : await commentsApi.addReply(bidId, targetId, commentId, body, addToBid)
    set((s) => {
      const prev = s.byTargetId[targetId] ?? []
      return {
        byTargetId: {
          ...s.byTargetId,
          [targetId]: prev.map((c) =>
            c.id === commentId
              ? { ...c, replies: [...c.replies, reply] }
              : c,
          ),
        },
        // A reply is activity, not a new thread — only the timestamp moves.
        ...(targetType !== 'draft'
          ? { countsByItemId: applyCountDelta(s.countsByItemId, targetId, {}) }
          : {}),
      }
    })
  },

  updateReply: async (bidId, targetId, commentId, replyId, body, targetType = 'draft') => {
    bumpMutationEpoch(targetId)
    const updated =
      targetType !== 'draft'
        ? await commentsApi.updateRequirementCommentReply(
            bidId,
            targetId,
            commentId,
            replyId,
            body,
            targetType,
          )
        : await commentsApi.updateReply(bidId, targetId, commentId, replyId, body)
    set((s) => {
      const prev = s.byTargetId[targetId] ?? []
      return {
        byTargetId: {
          ...s.byTargetId,
          [targetId]: prev.map((c) =>
            c.id === commentId
              ? {
                  ...c,
                  replies: c.replies.map((r: CommentReply) => (r.id === replyId ? updated : r)),
                }
              : c,
          ),
        },
      }
    })
  },

  removeReply: async (bidId, targetId, commentId, replyId, targetType = 'draft') => {
    bumpMutationEpoch(targetId)
    if (targetType !== 'draft') {
      await commentsApi.deleteRequirementCommentReply(
        bidId,
        targetId,
        commentId,
        replyId,
        targetType,
      )
    } else {
      await commentsApi.deleteReply(bidId, targetId, commentId, replyId)
    }
    set((s) => {
      const prev = s.byTargetId[targetId] ?? []
      return {
        byTargetId: {
          ...s.byTargetId,
          [targetId]: prev.map((c) =>
            c.id === commentId
              ? { ...c, replies: c.replies.filter((r: CommentReply) => r.id !== replyId) }
              : c,
          ),
        },
      }
    })
  },

  resolveWithAI: async (bidId, artifactId, commentId) => {
    // Epoch-bump like every other mutation: a silent poll GET already in
    // flight when the user clicks would otherwise land after the optimistic
    // "addressing" write below and wipe the chip until the next tick (~30s).
    bumpMutationEpoch(artifactId)
    const result = await commentsApi.resolveWithAI(bidId, artifactId, commentId)
    // Optimistically mark an addressing action in the local store.
    set((s) => {
      const prev = s.fixActionsByTargetId[artifactId] ?? {}
      return {
        fixActionsByTargetId: {
          ...s.fixActionsByTargetId,
          [artifactId]: {
            ...prev,
            [commentId]: {
              id: result.actionId,
              commentId,
              userId: '', // unknown locally; server authoritative
              agentThreadId: result.threadId,
              status: 'addressing',
              errorMessage: null,
              createdAt: new Date().toISOString(),
              completedAt: null,
            },
          },
        },
      }
    })
    return result
  },

  promoteFromFinding: async (bidId, artifactId, input) => {
    return get().create(bidId, artifactId, {
      anchor: {
        sectionId: input.sectionId,
        startPos: input.startPos,
        endPos: input.endPos,
        quotedText: input.quotedText,
      },
      body: input.body,
      sourceFindingRef: input.sourceFindingRef,
    })
  },

  /**
   * Open (or refresh) a view session for the comments surface — spec D4.
   *
   * Order matters: the snapshot is taken from the *current* watermark before
   * the POST, because the POST's own response is the advance. It is taken only
   * once per session, so the polling hook's re-fire (every 30 s while the tab
   * stays open) keeps closing the counterparty's edit window without ever
   * moving the cut-line under the reader's eyes.
   *
   * Failures are swallowed: nothing the user asked for is pending on it, and
   * the next poll re-fires.
   */
  markViewed: async (bidId, targetId, targetType = 'draft') => {
    set((s) => {
      if (Object.prototype.hasOwnProperty.call(s.viewSnapshotByTargetId, targetId)) return {}
      return {
        viewSnapshotByTargetId: {
          ...s.viewSnapshotByTargetId,
          [targetId]: s.watermarkByTargetId[targetId] ?? null,
        },
      }
    })

    try {
      const { watermark } =
        targetType !== 'draft'
          ? await commentsApi.markRequirementCommentsViewed(bidId, targetId, targetType)
          : await commentsApi.markCommentsViewed(bidId, targetId)
      set((s) => {
        // Keep the row badge's own watermark in step, so the compliance table's
        // unread dot clears the moment the drawer is opened.
        const counts = targetType !== 'draft' ? s.countsByItemId[targetId] : undefined
        return {
          watermarkByTargetId: { ...s.watermarkByTargetId, [targetId]: watermark },
          ...(counts
            ? { countsByItemId: { ...s.countsByItemId, [targetId]: { ...counts, watermark } } }
            : {}),
        }
      })
    } catch (err) {
      console.warn('[comments-store] Failed to mark comments viewed', err)
    }
  },

  /**
   * Close the view session (the comments surface lost focus or unmounted). The
   * next open re-snapshots against the advanced watermark, which is what clears
   * the previous visit's "New" pills.
   */
  clearViewSnapshot: (targetId) =>
    set((s) => {
      if (!Object.prototype.hasOwnProperty.call(s.viewSnapshotByTargetId, targetId)) return {}
      const next = { ...s.viewSnapshotByTargetId }
      delete next[targetId]
      return { viewSnapshotByTargetId: next }
    }),

  /**
   * Bid-wide thread counts for one extraction family, in one call.
   *
   * Silent by design: the counts drive a small badge on each row, and a failed
   * fetch should leave the last-good badges alone rather than blank the column
   * or raise an error the user did not ask for.
   */
  loadRequirementCounts: async (bidId, targetType = 'requirement') => {
    try {
      const counts = await commentsApi.fetchRequirementCommentCounts(bidId, targetType)
      // Laid *over* what is already there, not swapped for it: the Analysis
      // page shows several extraction tables that share this map, so a
      // wholesale replace would blank the other tables' badges on every
      // refetch. Item ids are UUIDs, so the families cannot collide on a key.
      const next: Record<string, RequirementCommentCounts> = {}
      for (const c of counts) next[c.itemId] = c
      set((s) => ({ countsByItemId: { ...s.countsByItemId, ...next } }))
    } catch (err) {
      console.warn('[comments-store] Failed to load requirement comment counts', err)
    }
  },

  focusComment: (commentId) => {
    clearFocusWatchers()
    set({ focusedCommentId: commentId })
    if (commentId === null) return

    focusExpiryTimer = setTimeout(() => {
      focusExpiryTimer = null
      get().focusComment(null)
    }, FOCUS_CLEAR_MS)

    focusArmTimer = setTimeout(() => {
      focusArmTimer = null
      focusPointerListener = () => get().focusComment(null)
      window.addEventListener('pointerdown', focusPointerListener)
    }, 0)
  },
  clearError: (targetId) => set((s) => ({ error: { ...s.error, [targetId]: null } })),
  reset: () => {
    clearFocusWatchers()
    set({
      byTargetId: {},
      fixActionsByTargetId: {},
      loading: {},
      error: {},
      watermarkByTargetId: {},
      viewSnapshotByTargetId: {},
      countsByItemId: {},
      permissions: {},
      promotedFromReview: {},
      focusedCommentId: null,
    })
  },
}))
